// This file is auto-generated. DO NOT EDIT.

import { useMemo } from 'react';
import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { useApiBaseUrl } from '../api';
import { SelectManyResponse, MutationResponse } from '../../api/model';
import { InternetDevice } from '../../model/internet-device-model';
import { InternetDeviceWithRefs } from '../../model/internet-device-api';
import {
  searchInternetDevices,
  SearchInternetDevicesParams,
  selectInternetDeviceById,
  SelectInternetDeviceByIdParams,
  selectInternetDeviceByIdentifier,
  SelectInternetDeviceByIdentifierParams,
  selectInternetDeviceByPublicId,
  SelectInternetDeviceByPublicIdParams,
  createInternetDevice,
  updateInternetDevice,
  deleteInternetDevice,
  aggregateInternetDevices,
  AggregateInternetDeviceParams,
  InternetDeviceAggregateResponse,
} from '../../api/internet-device-endpoints';
import { ApiError } from '../../api/errors';

type UseSearchInternetDevicesProps = Omit<SearchInternetDevicesParams, 'baseUrl'> & {
  queryName?: string;
  queryKey?: any[];
};

type SearchQueryOptions = Omit<
  UseQueryOptions<
    SelectManyResponse<InternetDeviceWithRefs>,
    ApiError,
    SelectManyResponse<InternetDeviceWithRefs>,
    any[]
  >,
  'initialData'
>;

export function useSearchInternetDevices(
  { queryKey, queryName, ...params }: UseSearchInternetDevicesProps,
  queryOptions?: SearchQueryOptions,
) {
  const baseUrl = useApiBaseUrl();

  const memoizedQueryKey = useMemo(() => {
    if (queryKey) {
      return queryKey;
    }
    const keys = Object.keys(params.query);
    keys.sort();
    const searchKey = keys.map(
      (key) =>
        `${key}=${JSON.stringify(params.query[key as keyof SearchInternetDevicesParams['query']])}`,
    );
    return ['searchInternetDevices', queryName, ...searchKey];
  }, [queryName, queryKey, params.query]);

  return useQuery({
    ...queryOptions,
    queryKey: memoizedQueryKey,
    queryFn: () => searchInternetDevices({ baseUrl, ...params }),
  });
}
type UseSelectInternetDeviceByIdProps = Omit<SelectInternetDeviceByIdParams, 'baseUrl'> & {
  queryName?: string;
  queryKey?: any[];
};

type SelectInternetDeviceByIdOptions = Omit<
  UseQueryOptions<InternetDeviceWithRefs, ApiError, InternetDeviceWithRefs, any[]>,
  'initialData'
>;

export function useSelectInternetDeviceById(
  { queryKey, queryName, ...params }: UseSelectInternetDeviceByIdProps,
  queryOptions?: SelectInternetDeviceByIdOptions,
) {
  const baseUrl = useApiBaseUrl();

  const memoizedQueryKey = useMemo(() => {
    return queryKey || ['selectInternetDeviceById', queryName, params.id];
  }, [queryKey, queryName, params.id]);

  return useQuery({
    ...queryOptions,
    queryKey: memoizedQueryKey,
    queryFn: () => selectInternetDeviceById({ baseUrl, ...params }),
  });
}
type UseSelectInternetDeviceByIdentifierProps = Omit<
  SelectInternetDeviceByIdentifierParams,
  'baseUrl'
> & {
  queryName?: string;
  queryKey?: any[];
};

type SelectInternetDeviceByIdentifierOptions = Omit<
  UseQueryOptions<InternetDeviceWithRefs, ApiError, InternetDeviceWithRefs, any[]>,
  'initialData'
>;

export function useSelectInternetDeviceByIdentifier(
  { queryKey, queryName, ...params }: UseSelectInternetDeviceByIdentifierProps,
  queryOptions?: SelectInternetDeviceByIdentifierOptions,
) {
  const baseUrl = useApiBaseUrl();

  const memoizedQueryKey = useMemo(() => {
    return queryKey || ['selectInternetDeviceByIdentifier', queryName, params.identifier];
  }, [queryKey, queryName, params.identifier]);

  return useQuery({
    ...queryOptions,
    queryKey: memoizedQueryKey,
    queryFn: () => selectInternetDeviceByIdentifier({ baseUrl, ...params }),
  });
}
type UseSelectInternetDeviceByPublicIdProps = Omit<
  SelectInternetDeviceByPublicIdParams,
  'baseUrl'
> & {
  queryName?: string;
  queryKey?: any[];
};

type SelectInternetDeviceByPublicIdOptions = Omit<
  UseQueryOptions<InternetDeviceWithRefs, ApiError, InternetDeviceWithRefs, any[]>,
  'initialData'
>;

export function useSelectInternetDeviceByPublicId(
  { queryKey, queryName, ...params }: UseSelectInternetDeviceByPublicIdProps,
  queryOptions?: SelectInternetDeviceByPublicIdOptions,
) {
  const baseUrl = useApiBaseUrl();

  const memoizedQueryKey = useMemo(() => {
    return queryKey || ['selectInternetDeviceByPublicId', queryName, params.publicId];
  }, [queryKey, queryName, params.publicId]);

  return useQuery({
    ...queryOptions,
    queryKey: memoizedQueryKey,
    queryFn: () => selectInternetDeviceByPublicId({ baseUrl, ...params }),
  });
}

export type InternetDeviceMutationOptions = {
  onAfterCommit?: () => Promise<void>;
};

export function useCreateInternetDevice(options: InternetDeviceMutationOptions = {}) {
  const baseUrl = useApiBaseUrl();
  return useMutation<MutationResponse<InternetDevice>, ApiError, InternetDevice>(
    async (internetDevice: InternetDevice) => {
      const res = await createInternetDevice({ baseUrl, data: internetDevice });
      if (options.onAfterCommit) {
        await options.onAfterCommit();
      }
      return res;
    },
  );
}

export function useUpdateInternetDevice(options: InternetDeviceMutationOptions = {}) {
  const baseUrl = useApiBaseUrl();
  return useMutation<MutationResponse<InternetDevice>, ApiError, InternetDevice>(
    async (internetDevice: InternetDevice) => {
      const res = await updateInternetDevice({ baseUrl, data: internetDevice });
      if (options.onAfterCommit) {
        await options.onAfterCommit();
      }
      return res;
    },
  );
}

export function useDeleteInternetDevice(options: InternetDeviceMutationOptions = {}) {
  const baseUrl = useApiBaseUrl();
  return useMutation<void, ApiError, string>(async (id: string) => {
    const res = await deleteInternetDevice({ baseUrl, id });
    if (options.onAfterCommit) {
      await options.onAfterCommit();
    }
    return res;
  });
}

type UseAggregateInternetDevicesProps = Omit<AggregateInternetDeviceParams, 'baseUrl'> & {
  queryName?: string;
  queryKey?: any[];
};

type InternetDeviceAggregateQueryOptions = Omit<
  UseQueryOptions<
    InternetDeviceAggregateResponse,
    ApiError,
    InternetDeviceAggregateResponse,
    any[]
  >,
  'initialData'
>;

export function useAggregateInternetDevices(
  { queryKey, queryName, ...params }: UseAggregateInternetDevicesProps,
  queryOptions?: InternetDeviceAggregateQueryOptions,
) {
  const baseUrl = useApiBaseUrl();

  const memoizedQueryKey = useMemo(() => {
    if (queryKey) {
      return queryKey;
    }
    const queryKeys = Object.keys(params.query);
    queryKeys.sort();
    const searchKey = queryKeys.map(
      (key) =>
        `${key}=${JSON.stringify(params.query[key as keyof AggregateInternetDeviceParams['query']])}`,
    );
    const fieldKeys = params.fields.map((f) => `${f.field}_${f.method}`);
    const groupByKeys = params.groupBy.join(',');
    return ['aggregateInternetDevices', queryName, ...searchKey, ...fieldKeys, groupByKeys];
  }, [queryName, queryKey, params.query, params.fields, params.groupBy]);

  return useQuery({
    ...queryOptions,
    queryKey: memoizedQueryKey,
    queryFn: () => aggregateInternetDevices({ baseUrl, ...params }),
  });
}
